import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft, Plus, Trash2, Send, DollarSign, Percent,
  Save, FileText, Package, Wrench, MoreHorizontal,
  TrendingUp, TrendingDown, ChevronDown, ChevronUp
} from "lucide-react";

const QuoteBuilderPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { quoteId } = useParams();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quote, setQuote] = useState(null);
  const [showExpenses, setShowExpenses] = useState(false);
  const [newExpense, setNewExpense] = useState({ expense_type: "materials", description: "", amount: "" });
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [quickClient, setQuickClient] = useState({ name: "", email: "", phone: "", password: "Welcome123!" });

  // Form state
  const [formData, setFormData] = useState({
    client_id: "",
    title: "",
    description: "",
    notes: "",
    valid_days: 30,
    line_items: [{ description: "", cost: "", price: "" }]
  });

  useEffect(() => {
    fetchClients();
    if (quoteId) fetchQuote();
  }, [quoteId]);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchQuote = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}`, { withCredentials: true });
      const q = res.data;
      setQuote(q);
      setFormData({
        client_id: q.client_id,
        title: q.title,
        description: q.description || "",
        notes: q.notes || "",
        valid_days: q.valid_days || 30,
        line_items: q.line_items.map(li => ({
          description: li.description,
          cost: li.cost.toString(),
          price: li.price.toString()
        }))
      });
    } catch (err) {
      toast.error("Failed to load quote");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: "", cost: "", price: "" }]
    }));
  };

  const removeLineItem = (idx) => {
    if (formData.line_items.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== idx)
    }));
  };

  const updateLineItem = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item
      )
    }));
  };

  // Calculations
  const totalCost = formData.line_items.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
  const totalPrice = formData.line_items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
  const profit = totalPrice - totalCost;
  const profitPercent = totalCost > 0 ? ((profit / totalCost) * 100).toFixed(1) : 0;
  const totalExpenses = (quote?.expenses || []).reduce((sum, e) => sum + e.amount, 0);
  const netProfit = profit - totalExpenses;

  const handleSave = async () => {
    if (!formData.client_id || !formData.title || formData.line_items.some(li => !li.description || !li.price)) {
      toast.error("Please fill in client, title, and all line item descriptions and prices");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        line_items: formData.line_items.map(li => ({
          description: li.description,
          cost: parseFloat(li.cost) || 0,
          price: parseFloat(li.price) || 0
        }))
      };

      if (quoteId) {
        await axios.patch(`${API}/quotes/${quoteId}`, payload, { withCredentials: true });
        toast.success("Quote updated!");
        fetchQuote();
      } else {
        const res = await axios.post(`${API}/quotes`, payload, { withCredentials: true });
        toast.success("Quote created!");
        navigate(`/admin/quotes/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  const handleSendToClient = async () => {
    if (!quoteId) return;
    try {
      await axios.post(`${API}/quotes/${quoteId}/send`, {}, { withCredentials: true });
      toast.success("Quote sent to client!");
      fetchQuote();
    } catch (err) {
      toast.error("Failed to send quote");
    }
  };

  const handleConvertToInvoice = async () => {
    if (!quoteId) return;
    try {
      const res = await axios.post(`${API}/quotes/${quoteId}/convert-to-invoice`, {}, { withCredentials: true });
      toast.success(`Invoice ${res.data.invoice_number} created!`);
      fetchQuote();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert to invoice");
    }
  };

  const handleAddExpense = async () => {
    if (!newExpense.description || !newExpense.amount) {
      toast.error("Fill in expense description and amount");
      return;
    }
    try {
      await axios.post(`${API}/quotes/${quoteId}/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount)
      }, { withCredentials: true });
      toast.success("Expense added!");
      setNewExpense({ expense_type: "materials", description: "", amount: "" });
      fetchQuote();
    } catch (err) {
      toast.error("Failed to add expense");
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/quotes/${quoteId}/expenses/${expenseId}`, { withCredentials: true });
      toast.success("Expense deleted");
      fetchQuote();
    } catch (err) {
      toast.error("Failed to delete expense");
    }
  };

  const handleDeleteQuote = async () => {
    if (!window.confirm("Delete this quote permanently?")) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}`, { withCredentials: true });
      toast.success("Quote deleted");
      navigate("/admin");
    } catch (err) {
      toast.error("Failed to delete quote");
    }
  };

  const handleQuickCreateClient = async () => {
    if (!quickClient.name || !quickClient.email || !quickClient.phone) {
      toast.error("Name, email, and phone required");
      return;
    }
    try {
      const res = await axios.post(`${API}/clients`, quickClient, { withCredentials: true });
      toast.success("Client created!");
      setShowNewClientForm(false);
      await fetchClients();
      // Auto-select the new client
      const refreshed = await axios.get(`${API}/clients`, { withCredentials: true });
      const newC = refreshed.data.find(c => c.email === quickClient.email);
      if (newC) setFormData(prev => ({ ...prev, client_id: newC.id }));
      setQuickClient({ name: "", email: "", phone: "", password: "Welcome123!" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create client");
    }
  };

  if (loading) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  const expenseTypeIcon = (type) => {
    if (type === "materials") return <Package size={14} className="text-blue-400" />;
    if (type === "labor") return <Wrench size={14} className="text-green-400" />;
    return <MoreHorizontal size={14} className="text-yellow-400" />;
  };

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10]" data-testid="quote-builder-page">
      {/* Header */}
      <div className="bg-[#15161C] border-b border-[#2A2B32]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/admin" className="p-2 rounded-lg bg-[#1F2027] text-[#A0AAB2] hover:text-white transition-colors" data-testid="back-to-admin">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {quoteId ? `Edit Quote${quote?.quote_number ? ` - ${quote.quote_number}` : ""}` : "New Quote"}
                </h1>
                {quote && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    quote.status === "draft" ? "bg-gray-500/20 text-gray-400" :
                    quote.status === "sent" ? "bg-blue-500/20 text-blue-400" :
                    quote.status === "approved" ? "bg-green-500/20 text-green-400" :
                    quote.status === "signed" ? "bg-emerald-500/20 text-emerald-400" :
                    quote.status === "invoiced" ? "bg-purple-500/20 text-purple-400" :
                    "bg-red-500/20 text-red-400"
                  }`}>
                    {quote.status.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {quoteId && quote?.status === "draft" && (
                <button onClick={handleSendToClient} className="flex items-center space-x-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors" data-testid="send-quote-btn">
                  <Send size={16} />
                  <span>Send to Client</span>
                </button>
              )}
              {quoteId && quote?.status === "signed" && (
                <button onClick={handleConvertToInvoice} className="flex items-center space-x-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors" data-testid="convert-invoice-btn">
                  <FileText size={16} />
                  <span>Convert to Invoice</span>
                </button>
              )}
              <button onClick={handleSave} disabled={saving} className="flex items-center space-x-1.5 px-4 py-2 gradient-btn rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="save-quote-btn">
                <Save size={16} />
                <span>{saving ? "Saving..." : "Save Quote"}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Client & Info */}
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
          <h2 className="text-base font-semibold text-white mb-4">Quote Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A0AAB2] mb-1">Client *</label>
              <select
                value={formData.client_id}
                onChange={(e) => {
                  if (e.target.value === "__new__") {
                    setShowNewClientForm(true);
                  } else {
                    setFormData(prev => ({ ...prev, client_id: e.target.value }));
                  }
                }}
                className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white"
                data-testid="quote-client-select"
              >
                <option value="">Select client</option>
                <option value="__new__">+ New Client</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                ))}
              </select>
              {showNewClientForm && (
                <div className="mt-3 p-4 bg-[#1F2027] border border-[#FF6B35]/50 rounded-lg space-y-3" data-testid="quick-client-form">
                  <p className="text-[#FF6B35] text-sm font-semibold">Quick Add Client</p>
                  <input type="text" value={quickClient.name} onChange={e => setQuickClient(p => ({...p, name: e.target.value}))}
                    placeholder="Name *" className="w-full px-3 py-2 bg-[#15161C] border border-[#2A2B32] rounded-lg text-white text-sm" data-testid="quick-client-name" />
                  <input type="email" value={quickClient.email} onChange={e => setQuickClient(p => ({...p, email: e.target.value}))}
                    placeholder="Email *" className="w-full px-3 py-2 bg-[#15161C] border border-[#2A2B32] rounded-lg text-white text-sm" data-testid="quick-client-email" />
                  <input type="tel" value={quickClient.phone} onChange={e => setQuickClient(p => ({...p, phone: e.target.value}))}
                    placeholder="Phone *" className="w-full px-3 py-2 bg-[#15161C] border border-[#2A2B32] rounded-lg text-white text-sm" data-testid="quick-client-phone" />
                  <div className="flex space-x-2">
                    <button onClick={() => setShowNewClientForm(false)} className="px-3 py-1.5 bg-[#2A2B32] text-[#A0AAB2] rounded-lg text-sm">Cancel</button>
                    <button onClick={handleQuickCreateClient} className="px-3 py-1.5 gradient-btn rounded-lg text-sm font-semibold" data-testid="save-quick-client">Save & Select</button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm text-[#A0AAB2] mb-1">Job Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white"
                placeholder="e.g. Kitchen Renovation"
                data-testid="quote-title"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-[#A0AAB2] mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white resize-none"
                placeholder="Job description..."
                data-testid="quote-description"
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white">Line Items</h2>
            <button onClick={addLineItem} className="flex items-center space-x-1 px-3 py-1.5 bg-[#FF6B35]/20 text-[#FF6B35] rounded-lg text-sm hover:bg-[#FF6B35]/30 transition-colors" data-testid="add-line-item">
              <Plus size={16} />
              <span>Add Item</span>
            </button>
          </div>

          {/* Header */}
          <div className="hidden md:grid grid-cols-12 gap-3 mb-2 text-xs text-[#A0AAB2] font-medium px-1">
            <div className="col-span-5">Description</div>
            <div className="col-span-2 text-right">Cost (Internal)</div>
            <div className="col-span-2 text-right">Price (Client)</div>
            <div className="col-span-2 text-right">Markup</div>
            <div className="col-span-1"></div>
          </div>

          <div className="space-y-3">
            {formData.line_items.map((item, idx) => {
              const itemCost = parseFloat(item.cost) || 0;
              const itemPrice = parseFloat(item.price) || 0;
              const itemMarkup = itemCost > 0 ? (((itemPrice - itemCost) / itemCost) * 100).toFixed(0) : 0;
              return (
                <div key={idx} className="grid grid-cols-12 gap-3 items-center" data-testid={`line-item-${idx}`}>
                  <div className="col-span-12 md:col-span-5">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateLineItem(idx, "description", e.target.value)}
                      className="w-full px-3 py-2.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm"
                      placeholder="Item description"
                      data-testid={`line-item-desc-${idx}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2] text-sm">$</span>
                      <input
                        type="number"
                        value={item.cost}
                        onChange={(e) => updateLineItem(idx, "cost", e.target.value)}
                        className="w-full pl-7 pr-3 py-2.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm text-right"
                        placeholder="0.00"
                        data-testid={`line-item-cost-${idx}`}
                      />
                    </div>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2] text-sm">$</span>
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => updateLineItem(idx, "price", e.target.value)}
                        className="w-full pl-7 pr-3 py-2.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm text-right"
                        placeholder="0.00"
                        data-testid={`line-item-price-${idx}`}
                      />
                    </div>
                  </div>
                  <div className="col-span-3 md:col-span-2 text-right">
                    <span className={`text-sm font-medium ${Number(itemMarkup) >= 0 ? "text-green-400" : "text-red-400"}`}>
                      {itemMarkup}%
                    </span>
                  </div>
                  <div className="col-span-1">
                    <button onClick={() => removeLineItem(idx)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors" data-testid={`remove-line-item-${idx}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Financial Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <p className="text-[#A0AAB2] text-xs mb-1">Total Cost</p>
            <p className="text-white text-lg font-bold" data-testid="total-cost">${totalCost.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </div>
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <p className="text-[#A0AAB2] text-xs mb-1">Total Price</p>
            <p className="text-[#FF6B35] text-lg font-bold" data-testid="total-price">${totalPrice.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </div>
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <p className="text-[#A0AAB2] text-xs mb-1">Profit</p>
            <p className={`text-lg font-bold ${profit >= 0 ? "text-green-400" : "text-red-400"}`} data-testid="profit">${profit.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </div>
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <p className="text-[#A0AAB2] text-xs mb-1">Profit %</p>
            <p className={`text-lg font-bold ${Number(profitPercent) >= 0 ? "text-green-400" : "text-red-400"}`} data-testid="profit-percent">{profitPercent}%</p>
          </div>
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <p className="text-[#A0AAB2] text-xs mb-1">Expenses</p>
            <p className="text-yellow-400 text-lg font-bold" data-testid="total-expenses">${totalExpenses.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </div>
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <p className="text-[#A0AAB2] text-xs mb-1">Net Profit</p>
            <p className={`text-lg font-bold ${netProfit >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="net-profit">${netProfit.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </div>
        </div>

        {/* Expense Tracking (only for existing quotes) */}
        {quoteId && (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
            <button
              onClick={() => setShowExpenses(!showExpenses)}
              className="flex items-center justify-between w-full text-left"
              data-testid="toggle-expenses"
            >
              <h2 className="text-base font-semibold text-white">Expense Tracking</h2>
              {showExpenses ? <ChevronUp size={20} className="text-[#A0AAB2]" /> : <ChevronDown size={20} className="text-[#A0AAB2]" />}
            </button>

            {showExpenses && (
              <div className="mt-4 space-y-4">
                {/* Add expense form */}
                <div className="flex flex-col md:flex-row gap-3">
                  <select
                    value={newExpense.expense_type}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, expense_type: e.target.value }))}
                    className="px-3 py-2.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm md:w-40"
                    data-testid="expense-type-select"
                  >
                    <option value="materials">Materials</option>
                    <option value="labor">Labor</option>
                    <option value="misc">Misc</option>
                  </select>
                  <input
                    type="text"
                    value={newExpense.description}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                    className="flex-1 px-3 py-2.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm"
                    placeholder="Expense description"
                    data-testid="expense-description"
                  />
                  <div className="relative md:w-32">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2] text-sm">$</span>
                    <input
                      type="number"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                      className="w-full pl-7 pr-3 py-2.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm"
                      placeholder="0.00"
                      data-testid="expense-amount"
                    />
                  </div>
                  <button onClick={handleAddExpense} className="px-4 py-2.5 bg-[#FF6B35] text-white rounded-lg text-sm font-semibold hover:bg-[#FF6B35]/90 transition-colors" data-testid="add-expense-btn">
                    Add
                  </button>
                </div>

                {/* Expense list */}
                {(quote?.expenses || []).length === 0 ? (
                  <p className="text-[#A0AAB2] text-sm text-center py-4">No expenses recorded yet</p>
                ) : (
                  <div className="space-y-2">
                    {(quote?.expenses || []).map((exp) => (
                      <div key={exp.id} className="flex items-center justify-between bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3">
                        <div className="flex items-center space-x-3">
                          {expenseTypeIcon(exp.expense_type)}
                          <span className="text-xs uppercase font-medium text-[#A0AAB2] w-16">{exp.expense_type}</span>
                          <span className="text-white text-sm">{exp.description}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-white font-semibold text-sm">${exp.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                          <button onClick={() => handleDeleteExpense(exp.id)} className="p-1 text-red-400 hover:bg-red-500/20 rounded transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
          <label className="block text-sm text-[#A0AAB2] mb-2">Notes (internal)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows={2}
            className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white resize-none text-sm"
            placeholder="Internal notes..."
            data-testid="quote-notes"
          />
        </div>

        {/* Delete */}
        {quoteId && (
          <div className="flex justify-end">
            <button onClick={handleDeleteQuote} className="text-red-400 text-sm hover:underline" data-testid="delete-quote-btn">
              Delete this quote
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteBuilderPage;
